<h1 mat-dialog-title>Product Payroll Benefit Categories for {{data.companyName}}</h1>
<div mat-dialog-content>
  <div class="setting-mode-panel">
    <div class="setting-mode-buttons">
      <button
        *ngIf="canInherit()"
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'inherit'"
        (click)="setMode('inherit')">
        Use Parent Setting
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'none'"
        (click)="setMode('none')">
        No Categories For This Company
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'custom'"
        (click)="setMode('custom')">
        Set Custom Categories
      </button>
    </div>
    <p class="setting-mode-help" *ngIf="mode === 'inherit' && canInherit()">
      This company will use Product Payroll Benefit Categories from {{data.parentCompanyName || 'its parent company'}}.
    </p>
    <p class="setting-mode-help" *ngIf="mode === 'none'">
      This company will explicitly override the parent setting and have no payroll benefit categories.
    </p>
    <p class="setting-mode-help" *ngIf="mode === 'custom'">
      Set custom payroll benefit categories and subcategory labels for this company.
    </p>
  </div>

  <div class="subcategory-editor-panel" *ngIf="mode !== 'none'">
    <div class="subcategory-editor-header">
      <h2 class="subcategory-editor-title">Managed Subcategories</h2>
      <p class="subcategory-editor-copy">
        Keep the top-level tabs fixed and manage the section names that appear within each tab.
      </p>
    </div>

    <app-inlinemessage
      *ngIf="subcategoryEditorError"
      level="warning"
      [message]="subcategoryEditorError">
    </app-inlinemessage>

    <div class="subcategory-editor-grid">
      <div class="subcategory-editor-card" *ngFor="let category of payrollBenefitCategoryOptions">
        <div class="subcategory-editor-card-header">
          <h3>{{getCategoryLabel(category)}}</h3>
        </div>

        <div class="subcategory-definition-list" *ngIf="getSubcategoryDefinitions(category).length > 0; else noSubcategories">
          <div class="subcategory-definition-row" *ngFor="let definition of getSubcategoryDefinitions(category)">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="subcategory-definition-input">
              <mat-label>Label</mat-label>
              <input
                matInput
                [disabled]="!isCustomMode()"
                [value]="definition.label"
                (change)="renameSubcategory(category, definition.id, $any($event.target).value)">
            </mat-form-field>

            <button
              mat-icon-button
              type="button"
              color="warn"
              [disabled]="!isCustomMode() || !canDeleteSubcategory(category, definition.id)"
              [matTooltip]="canDeleteSubcategory(category, definition.id) ? 'Delete subcategory' : 'Reassign products before deleting this subcategory'"
              (click)="deleteSubcategory(category, definition.id)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>

        <ng-template #noSubcategories>
          <div class="subcategory-empty-state">No custom subcategories yet.</div>
        </ng-template>

        <div class="subcategory-add-row" *ngIf="isCustomMode()">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="subcategory-add-input">
            <mat-label>Add subcategory</mat-label>
            <input
              matInput
              [value]="subcategoryDraftByCategory[category]"
              (input)="updateSubcategoryDraft(category, $any($event.target).value)"
              (keydown.enter)="addSubcategory(category)">
          </mat-form-field>

          <button
            mat-stroked-button
            type="button"
            class="subcategory-add-button"
            (click)="addSubcategory(category)">
            Add
          </button>
        </div>
      </div>
    </div>
  </div>

  <app-inlinemessage
    *ngIf="items.length === 0"
    level="warning"
    message="No products selected for this company. Set Products first.">
  </app-inlinemessage>

  <table *ngIf="items.length > 0" class="table" mat-table [dataSource]="items">
    <ng-container matColumnDef="product">
      <th mat-header-cell *matHeaderCellDef>Product</th>
      <td class="cell" mat-cell *matCellDef="let item">
        {{item.vendorName}} - {{item.productName}}
      </td>
    </ng-container>

    <ng-container matColumnDef="productId">
      <th mat-header-cell *matHeaderCellDef>Product ID</th>
      <td class="cell" mat-cell *matCellDef="let item">
        {{item.id}}
      </td>
    </ng-container>

    <ng-container matColumnDef="category">
      <th mat-header-cell *matHeaderCellDef>Payroll Benefit Category</th>
      <td class="cell" mat-cell *matCellDef="let item">
        <div class="category-controls">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="category-select-field">
            <mat-select
              [value]="getMapping(item.id)?.category || ''"
              [disabled]="!isCustomMode()"
              (selectionChange)="setCategory(item.id, $event.value)">
              <mat-option value="">None</mat-option>
              <mat-option *ngFor="let category of payrollBenefitCategoryOptions" [value]="category">
                {{getCategoryLabel(category)}}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field
            *ngIf="hasSubcategoryOptions(getMapping(item.id)?.category || '')"
            appearance="outline"
            subscriptSizing="dynamic"
            class="subcategory-select-field">
            <mat-select
              placeholder="Subcategory (optional)"
              [value]="getMapping(item.id)?.subcategory || ''"
              [disabled]="!isCustomMode()"
              (selectionChange)="setSubcategory(item.id, $event.value)">
              <mat-option value="">None</mat-option>
              <mat-option
                *ngFor="let subcategory of getSubcategoryOptions(getMapping(item.id)?.category || '')"
                [value]="subcategory">
                {{getSubcategoryLabel(getMapping(item.id)?.category || '', subcategory)}}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="['product', 'productId', 'category']"></tr>
    <tr mat-row *matRowDef="let row; columns: ['product', 'productId', 'category']"></tr>
  </table>
</div>
<div class="buttons" mat-dialog-actions>
  <button mat-raised-button class="donebutton" color="primary" (click)="okClicked()">Done</button>
  <button mat-button (click)="onDismiss()">Cancel</button>
</div>
